import "./UserAccessDrawer.scss";
import {Button, Checkbox, Dropdown, Input, Skeleton} from "antd";
import {CloseOutlined, FilterOutlined, SearchOutlined} from "@ant-design/icons";
import {useEffect, useMemo, useState} from "react";
import {useRecoilValue} from "recoil";
import {MemberDto, MemberDtoRoleEnum} from "../../../../generated/dashboard-client";
import {locationsState} from "../../../../state/atoms";
import MembersApi from "../../../../api/MembersApi";
import {handleServerError} from "../../../../util/ErrorHandler";
import {TeamDrawer} from "./TeamDrawer";
import {PresetBadge} from "./PresetBadge";
import {Preset} from "./permissionModel";
import {LocationAccess, locationAccessList} from "./userAccess";

interface UserAccessDrawerProps {
  user: MemberDto;
  presets: Preset[];
  onEditPermissions: (user: MemberDto) => void;
  onClose: () => void;
}

interface PresetStat {
  key: string;
  label: string;
  level: string;
  count: number;
}

function presetStats(accesses: LocationAccess[]): PresetStat[] {
  const stats = new Map<string, PresetStat>();
  accesses.forEach(({badge}) => {
    const stat = stats.get(badge.key) ?? {key: badge.key, label: badge.label, level: badge.level ?? "none", count: 0};
    stat.count++;
    stats.set(badge.key, stat);
  });
  return Array.from(stats.values()).sort((a, b) => b.count - a.count);
}

export function UserAccessDrawer({user, presets, onEditPermissions, onClose}: UserAccessDrawerProps) {
  const locations = useRecoilValue(locationsState);
  const isAdmin = user.role === MemberDtoRoleEnum.Admin;
  const hasFullAccess = isAdmin || user.role === MemberDtoRoleEnum.Owner;
  const [loading, setLoading] = useState(!hasFullAccess);
  const [accesses, setAccesses] = useState<LocationAccess[]>([]);
  const [search, setSearch] = useState("");
  const [appliedKeys, setAppliedKeys] = useState<string[]>([]);
  const [draftKeys, setDraftKeys] = useState<string[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    if (hasFullAccess) return;
    MembersApi.getUserTeamLocationPermissions(user.id!)
      .then((resp) => setAccesses(locationAccessList(resp.data, locations, presets)))
      .catch(handleServerError)
      .finally(() => setLoading(false));
  }, [user.id, hasFullAccess, locations, presets]);

  const ownerLocations = useMemo(
    () => [...locations].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")),
    [locations]
  );
  const stats = useMemo(() => presetStats(accesses), [accesses]);

  const query = search.trim().toLowerCase();
  const visibleAccesses = accesses.filter((access) =>
    (appliedKeys.length === 0 || appliedKeys.includes(access.badge.key)) &&
    (!query || access.name.toLowerCase().includes(query) || access.city.toLowerCase().includes(query))
  );
  const visibleOwnerLocations = ownerLocations.filter((location) =>
    !query || (location.name ?? "").toLowerCase().includes(query) || (location.city ?? "").toLowerCase().includes(query)
  );

  const filterMenu = (
    <div className={"access-filter-menu"}>
      {stats.map((stat) =>
        <div className={"access-filter-option"} key={stat.key}>
          <Checkbox
            checked={draftKeys.includes(stat.key)}
            onChange={(ev) => setDraftKeys(ev.target.checked ? [...draftKeys, stat.key] : draftKeys.filter((k) => k !== stat.key))}
          >{stat.label}</Checkbox>
        </div>)}
      <div className={"access-filter-actions"}>
        <Button onClick={() => {
          setDraftKeys([]);
          setAppliedKeys([]);
          setFilterOpen(false);
        }}>Clear</Button>
        <Button type={"primary"} onClick={() => {
          setAppliedKeys(draftKeys);
          setFilterOpen(false);
        }}>Apply</Button>
      </div>
    </div>
  );

  return (
    <TeamDrawer label={`${user.name} access`} onClose={onClose}>
      {(close) => <>
        <div className={"td-header"}>
          <div>
            <h4>{user.name}</h4>
            <p>{user.email} • {user.role}</p>
          </div>
          <button className={"td-close"} aria-label={"Close"} onClick={close}><CloseOutlined/></button>
        </div>

        {hasFullAccess && <div className={`full-access-banner${isAdmin ? " admin" : ""}`}>
          <div className={"title"}>
            <span className={"bubble"}>ALL</span>
            <span className={"label"}>Full Access</span>
            {isAdmin && <span className={"admin-tag"}>Admin</span>}
          </div>
          <p>Every permission at every location, including locations added in the future.</p>
        </div>}

        {!hasFullAccess && !loading && <div className={"access-stats"}>
          <div className={"stat"}><strong>{accesses.length}</strong><span>Locations</span></div>
          {stats.map((stat) =>
            <div className={`stat level-${stat.level}`} key={stat.key}>
              <strong>{stat.count}</strong><span>{stat.label}</span>
            </div>)}
        </div>}

        <div className={"td-body"}>
          <div className={"td-search"}>
            <Input prefix={<SearchOutlined/>} placeholder={hasFullAccess ? "Search locations" : "Search locations by name or city"}
                   value={search} onChange={(ev) => setSearch(ev.target.value)} allowClear/>
            {!hasFullAccess && <Dropdown
              trigger={["click"]}
              open={filterOpen}
              onOpenChange={(open) => {
                setFilterOpen(open);
                if (open) setDraftKeys(appliedKeys);
              }}
              dropdownRender={() => filterMenu}
            >
              <button className={"td-close"} aria-label={"Filter locations"}><FilterOutlined/></button>
            </Dropdown>}
          </div>

          {loading && <Skeleton/>}

          {hasFullAccess && !query && <div className={"locations-caption"}>{ownerLocations.length} Locations</div>}

          {hasFullAccess && visibleOwnerLocations.map((location) =>
            <div className={"access-row"} key={location.internalId}>
              <div>
                <strong>{location.name}</strong>
                <span>{[location.city, location.state].filter(Boolean).join(", ")}</span>
              </div>
            </div>)}

          {!hasFullAccess && !loading && visibleAccesses.map((access) =>
            <div className={"access-row"} key={access.locationId}>
              <div>
                <strong>{access.name}</strong>
                <span>{access.city}</span>
              </div>
              <PresetBadge badge={access.badge}/>
            </div>)}

          {!loading && (hasFullAccess ? visibleOwnerLocations.length === 0 : visibleAccesses.length === 0) &&
            <div className={"td-empty"}>No locations found</div>}
        </div>

        {!hasFullAccess && <div className={"td-footer"}>
          <Button type={"primary"} onClick={() => onEditPermissions(user)}>Edit Permissions</Button>
        </div>}
      </>}
    </TeamDrawer>
  );
}
